// Shareable article deep links, carried in the host page's URL hash:
//
//   https://partner.example/some/page#skycell-kb=<slug>
//
// The token is regex-parsed out of the hash (never routed), so it coexists with
// host SPA hash routing (`#/orders?skycell-kb=slug`, `#tab=x&skycell-kb=slug`).
// The widget only ever READS the hash — it never writes location.hash, so host
// routers are never disturbed.

export const KB_HASH_PARAM = "skycell-kb";

// Matches `skycell-kb=<value>` preceded by `#`, `&` or `?` anywhere in the hash.
const TOKEN_RE = /[#&?]skycell-kb=([^&#\s]+)/;

/** Extract the article slug from a location hash, or null if none/undecodable. */
export function parseDeepLink(hash: string): string | null {
  const token = hash.match(TOKEN_RE)?.[1];
  if (!token) return null;
  try {
    return decodeURIComponent(token);
  } catch {
    return null; // malformed percent-encoding
  }
}

/**
 * Build a shareable URL for `slug` from the current `href`: any existing
 * skycell-kb token is stripped from the hash, then the new one is appended —
 * as the whole hash if nothing else is there, with `&` otherwise (preserving
 * host SPA hash routes).
 */
export function buildShareUrl(href: string, slug: string): string {
  const hashIdx = href.indexOf("#");
  const base = hashIdx === -1 ? href : href.slice(0, hashIdx);
  const hash = hashIdx === -1 ? "" : href.slice(hashIdx + 1);
  // Strip an existing token (with its preceding separator when mid-hash).
  const rest = hash
    .replace(new RegExp(`[&?]${KB_HASH_PARAM}=[^&#\\s]*`), "")
    .replace(new RegExp(`^${KB_HASH_PARAM}=[^&#\\s]*&?`), "");
  const token = `${KB_HASH_PARAM}=${encodeURIComponent(slug)}`;
  return rest ? `${base}#${rest}&${token}` : `${base}#${token}`;
}

/**
 * The same article's address in the standalone Knowledge Base app — the footer's
 * "Open in the Knowledge Base" link, and what the header's Copy link button
 * copies. `base` is the app root (config.standaloneUrl,
 * always trailing-slashed); the reader route is `kb/<slug>` for every deployment
 * (src/spa/router.tsx), embedded or standalone.
 *
 * Returns null when there is no base to resolve against, so the caller simply
 * doesn't render the link.
 */
export function buildStandaloneUrl(
  base: string | undefined,
  slug: string,
): string | null {
  if (!base || !slug) return null;
  try {
    return new URL(`kb/${encodeURIComponent(slug)}`, base).href;
  } catch {
    return null;
  }
}

/**
 * What the header's Copy link button puts on the clipboard: the article in the
 * standalone Knowledge Base app, which is the address that still resolves for
 * someone who is not on this host page — or in this product — at all.
 *
 * The host-page deep link is the fallback, for an embed that resolves no
 * standalone base (`data-standalone-url` unset AND the script origin
 * undiscoverable). It reopens the widget in place, which only helps a recipient
 * who can already load this page, but it beats copying nothing.
 */
export function buildCopyLink(
  standaloneBase: string | undefined,
  href: string,
  slug: string,
): string {
  return buildStandaloneUrl(standaloneBase, slug) ?? buildShareUrl(href, slug);
}
